import {
  Alert,
  Button,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";
import { BreadcrumbsPath } from "components/commons/BreadscrumbsPath";
import { ImagioOpticalMaterialDataLabel } from "components/dataDisplay/ImagioOpticalMaterialDataLabel";
import { useAuthUser } from "hooks/authentication";
import type { ColorSearchSettings } from "models/colors";
import { materialSection } from "pages/commons/constants";
import { useDataset } from "providers/DatasetProvider";
import { type Dispatch, type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { authenNavigate } from "service/utils/authen-navigate";
import {
  clearDatasetDependentFilters,
  resolveDataset,
} from "service/utils/dataset";
import { checkInternalUser } from "service/utils/get-user-type";

export interface ColorSearchSettingsMenuProps {
  settings: ColorSearchSettings;
  onSave: Dispatch<ColorSearchSettings>;
}

export const UserSettings: FC<ColorSearchSettingsMenuProps> = ({
  settings,
  onSave,
}) => {
  authenNavigate();
  const [unsavedSettings, setUnsavedSettings] = useState(settings);
  const [isSave, setIsSave] = useState(false);
  const { user } = useAuthUser();
  const isInternal: boolean = checkInternalUser(user, settings);
  const { dataset: activeDataset, isSwitchEnabled } = useDataset();
  // Judged on the unsaved External View choice: toggling it on hides the row
  // immediately, and toggling it off lets an internal user pick a dataset.
  const isInternalAfterSave = checkInternalUser(user, unsavedSettings);
  const canChooseDataset = isSwitchEnabled && isInternalAfterSave;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const patchSettings = (patch: Partial<ColorSearchSettings>) => {
    setUnsavedSettings((old) => ({ ...old, ...patch }));
    setIsSave(false);
  };

  const handleSave = () => {
    onSave(unsavedSettings);
    setIsSave(true);
    const datasetChanged =
      activeDataset !==
      resolveDataset({
        dataset: unsavedSettings.dataset,
        isInternal: isInternalAfterSave,
        flagEnabled: isSwitchEnabled,
      });
    setTimeout(() => {
      setIsSave(false);
      handleBack(datasetChanged);
    }, 1000);
  };

  // The search page restores its filters from this route's query string, so a
  // changed dataset drops the filters whose options only exist in one dataset.
  const handleBack = (datasetChanged: boolean) => {
    const prevURL = localStorage.getItem("rememberRoute");
    if (prevURL && prevURL !== null) {
      navigate(
        datasetChanged ? clearDatasetDependentFilters(prevURL) : prevURL,
      );
    } else {
      navigate("/");
    }
    localStorage.removeItem("rememberRoute");
  };

  const validationError = Object.values(unsavedSettings).every(
    (v) => v === false,
  )
    ? `${t("usst_setting_selected_valid")}`
    : undefined;
  return (
    <Grid
      container
      mt={0}
      mb={0}
      rowSpacing={1.5}
      columnSpacing={4}
      sx={{
        backgroundColor: "#fff",
        padding: {
          xs: "var(--spacing16, 16px)",
          md: "var(--spacing24, 24px) var(--spacing48, 48px)",
          lg: "24px 96px",
        },
        position: "relative",
      }}
    >
      {isSave && (
        <Alert sx={{ position: "absolute", right: "0" }} severity="success">
          {t("usst_saved_successfully")}
        </Alert>
      )}
      {validationError && (
        <Alert sx={{ position: "absolute", right: "24px" }} severity="error">
          {validationError}
        </Alert>
      )}
      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
        <BreadcrumbsPath pathName={t("usst_breadcrumbs_path")} />
      </Grid>
      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
        <Typography
          variant="label"
          component="p"
          sx={{
            color: "#4D4D4D",
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            fontWeight: 400,
          }}
        >
          {t("usst_description_text")}
        </Typography>
        <Typography
          sx={{
            color: "#4D4D4D",
            fontFamily: 'var(--font-family, "AktivGrotesk")',
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            fontWeight: 500,
            marginTop: 1.5,
          }}
          variant="label"
          component="p"
        >
          {t("usst_visible_category")}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 12, xl: 12 }}>
        <List
          sx={{
            "& >li.MuiListItem-root": { width: { xs: "100%", md: "45%" } },
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
        >
          <ListItem disableGutters sx={{ marginRight: "auto" }}>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_external_view_primary")}
              secondary={t("usst_external_view_secondary")}
            />
            <Switch
              checked={unsavedSettings.externalViewVisible}
              onChange={(e) =>
                patchSettings({ externalViewVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_optical_material_data_primary")}
              secondary={
                <>
                  {t("usst_optical_material_data_secondary")}{" "}
                  <ImagioOpticalMaterialDataLabel />
                </>
              }
            />
            <Switch
              disabled={unsavedSettings.externalViewVisible}
              checked={unsavedSettings.opticalMaterialDataVisible}
              onChange={(e) =>
                patchSettings({ opticalMaterialDataVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          <ListItem disableGutters sx={{ marginRight: "auto" }}>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_color_material_primary")}
              secondary={t("usst_color_material_secondary")}
            />
            <Switch
              disabled={unsavedSettings.externalViewVisible}
              checked={unsavedSettings.colorMaterialVisible}
              onChange={(e) =>
                patchSettings({ colorMaterialVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_customer_oem_primary")}
              secondary={t("usst_customer_oem_secondary")}
            />
            <Switch
              disabled={unsavedSettings.externalViewVisible}
              checked={unsavedSettings.customerOemVisible}
              onChange={(e) =>
                patchSettings({ customerOemVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          <ListItem disableGutters sx={{ marginRight: "auto" }}>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_availability_primary")}
              secondary={t("usst_availability_secondary")}
            />
            <Switch
              disabled={unsavedSettings.externalViewVisible}
              checked={unsavedSettings.availabilityVisible}
              onChange={(e) =>
                patchSettings({ availabilityVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_competitor_primary")}
              secondary={t("usst_competitor_secondary")}
            />
            <Switch
              disabled={unsavedSettings.externalViewVisible}
              checked={unsavedSettings.competitorVisible}
              onChange={(e) =>
                patchSettings({ competitorVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          <ListItem disableGutters sx={{ marginRight: "auto" }}>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_color_specifications_primary")}
              secondary={t("usst_color_specifications_secondary")}
            />
            <Switch
              disabled={unsavedSettings.externalViewVisible}
              checked={unsavedSettings.colorSpecificationsVisible}
              onChange={(e) =>
                patchSettings({ colorSpecificationsVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              sx={{
                "& span": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 500,
                  color: "#4D4D4D",
                },
                "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
              }}
              primary={t("usst_digital_samples_primary")}
              secondary={t("usst_digital_samples_secondary")}
            />
            <Switch
              disabled={unsavedSettings.externalViewVisible}
              checked={unsavedSettings.digitalSampleVisible}
              onChange={(e) =>
                patchSettings({ digitalSampleVisible: e.target.checked })
              }
              edge="end"
            />
          </ListItem>
          {canChooseDataset && (
            <ListItem disableGutters sx={{ marginRight: "auto" }}>
              <ListItemText
                sx={{
                  "& span": {
                    fontFamily: 'var(--font-family, "AktivGrotesk")',
                    fontWeight: 500,
                    color: "#4D4D4D",
                  },
                  "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
                }}
                primary={t("usst_dataset_primary")}
                secondary={t("usst_dataset_secondary")}
              />
              <Switch
                checked={unsavedSettings.dataset === "cleaned"}
                onChange={(e) =>
                  patchSettings({
                    dataset: e.target.checked ? "cleaned" : "original",
                  })
                }
                slotProps={{
                  input: { "aria-label": t("usst_dataset_primary") },
                }}
                edge="end"
              />
            </ListItem>
          )}
          <ListItem disableGutters>
            <div>
              <ListItemText
                sx={{
                  "& span": {
                    fontFamily: 'var(--font-family, "AktivGrotesk")',
                    fontWeight: 500,
                    color: "#4D4D4D",
                  },
                  "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
                }}
                primary={t("usst_section_material_sub_header")}
              />
              <ListItemText
                sx={{
                  "& span": {
                    fontFamily: 'var(--font-family, "AktivGrotesk")',
                    fontWeight: 500,
                    color: "#4D4D4D",
                  },
                  "& p": { fontSize: "var(--body-b3-12-regular-size, 12px)" },
                }}
                primary={t("usst_section_material_primary")}
                secondary={t("usst_section_material_secodary")}
              />
              <RadioGroup
                value={unsavedSettings.materialSection}
                onChange={(e) => {
                  patchSettings({ materialSection: e.target.value });
                }}
              >
                {(isInternal ? materialSection : materialSection.slice(1)).map(
                  (i) => (
                    <FormControlLabel
                      value={i.value}
                      control={<Radio size="small" />}
                      label={t(i.label)}
                    />
                  ),
                )}
              </RadioGroup>
            </div>
          </ListItem>
        </List>
        <Button
          sx={{
            width: "124px",
            height: "32px",
            borderRadius: "2px",
            border: "none",
            boxShadow: "0px 2px 0px 0px rgba(0, 0, 0, 0.04)",
            whiteSpace: "nowrap",
          }}
          variant="contained"
          disabled={settings === unsavedSettings || Boolean(validationError)}
          onClick={handleSave}
        >
          {t("usst_save_change")}
        </Button>
      </Grid>
    </Grid>
  );
};
